import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { SITE } from "@/config/site";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import BrandLogo from "@/components/feature/BrandLogo";

const primaryNav = [
  { label: "Home", to: "/", end: true },
  { label: "Featured", to: "/featured-listings" },
  { label: "All Listings", to: "/directory" },
  { label: "Location Wise", to: "/locations" },
  { label: "Essentials", to: "/categories" },
  { label: "Tourist Spots", to: "/tourist-spots" },
];

const moreLinks = [
  { label: "Listing Plans", to: "/listing-plans", icon: "ri-price-tag-3-line" },
  { label: "Why Choose Us", to: "/why-us", icon: "ri-award-line" },
  { label: "FAQs", to: "/faqs", icon: "ri-question-line" },
];

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Header() {
  const { settings } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<"az" | "more" | null>(null);

  const close = () => setMenu(null);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer ${
      isActive ? "text-primary-700" : "text-foreground-700 hover:text-primary-700"
    }`;

  return (
    <>
      <div className="hidden bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-6 py-2 text-xs">
          <p className="flex items-center gap-2">
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-400">
              <i className="ri-map-pin-2-fill text-[13px]"></i>
            </span>
            Your trusted business directory — {SITE.regions.join(" • ")}
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/emergency-services"
              className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-red-400 transition-colors hover:text-red-300"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-alarm-warning-line text-[13px]"></i>
              </span>
              24×7 Emergency
            </Link>
            <span className="hidden text-background-300 lg:inline">GSTIN: {SITE.gstin}</span>
            <a
              href={`mailto:${SITE.email}`}
              className="inline-flex items-center gap-1.5 transition-colors hover:text-primary-300"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-mail-line text-[13px]"></i>
              </span>
              {SITE.email}
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex shrink-0 items-center" aria-label={settings.siteName}>
            <BrandLogo className="h-12 md:h-14" />
          </Link>

          <nav className="hidden items-center gap-x-0.5 xl:flex">
            {primaryNav.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={navLinkClass}>
                {l.label}
              </NavLink>
            ))}

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenu(menu === "az" ? null : "az")}
                aria-haspopup="true"
                aria-expanded={menu === "az"}
                className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  menu === "az" ? "text-primary-700" : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                A to Z Categories
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>
              {menu === "az" && (
                <>
                  <button type="button" aria-hidden="true" tabIndex={-1} onClick={close} className="fixed inset-0 z-40 cursor-default" />
                  <div className="absolute left-0 top-full z-50 mt-1 w-[400px] rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-start gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
                        <i className="ri-sort-alphabet-asc text-xl"></i>
                      </span>
                      <div className="min-w-0">
                        <p className="font-heading text-base font-bold text-foreground-950">Jump to a letter</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                          Every business category on {settings.siteName}, A to Z.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-7 gap-1.5">
                      {ALPHABET.map((letter) => (
                        <Link
                          key={letter}
                          to={`/categories-a-z?letter=${letter}`}
                          onClick={close}
                          aria-label={`Browse categories starting with ${letter}`}
                          className="flex h-10 items-center justify-center rounded-md bg-background-100 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-800 cursor-pointer"
                        >
                          {letter}
                        </Link>
                      ))}
                    </div>

                    <div className="mt-4 border-t border-background-200 pt-3">
                      <Link
                        to="/categories-a-z"
                        onClick={close}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                      >
                        Browse the full A to Z index
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenu(menu === "more" ? null : "more")}
                aria-haspopup="true"
                aria-expanded={menu === "more"}
                className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  menu === "more" ? "text-primary-700" : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                More
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>
              {menu === "more" && (
                <>
                  <button type="button" aria-hidden="true" tabIndex={-1} onClick={close} className="fixed inset-0 z-40 cursor-default" />
                  <div className="absolute right-0 top-full z-50 mt-1 w-60 rounded-lg border border-background-200 bg-background-50 p-1.5">
                    {moreLinks.map((l) => (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={close}
                        className="flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
                      >
                        <i className={`${l.icon} text-primary-600`}></i>
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/login"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-100 px-4 py-2.5 text-sm font-semibold text-accent-900 transition-colors duration-200 hover:bg-accent-200 sm:inline-flex cursor-pointer"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-user-line text-[13px]"></i>
              </span>
              Members Login
            </Link>
            <Link
              to="/list-your-business"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-primary-600 md:inline-flex cursor-pointer"
            >
              Add your Listing
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle navigation menu"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 xl:hidden cursor-pointer"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-3 xl:hidden">
            <div className="grid grid-cols-2 gap-1">
              {[...primaryNav, ...moreLinks].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={"end" in l ? (l as { end?: boolean }).end : undefined}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2.5 text-sm font-medium cursor-pointer ${
                      isActive ? "bg-primary-100 text-primary-800" : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/categories-a-z"
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                Categories A–Z
              </NavLink>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent-100 px-3 py-2.5 text-sm font-semibold text-accent-900"
              >
                <i className="ri-user-line"></i> Members Login
              </Link>
              <Link
                to="/list-your-business"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-3 py-2.5 text-sm font-semibold text-foreground-950"
              >
                <i className="ri-add-circle-line"></i> Add Listing
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}